import type { Auth } from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  type Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { firestore } from './firebase'; // Assuming you have a firebase config file

export interface Notification {
  id?: string;
  userId: string;
  title: string;
  message: string;
  timestamp: Timestamp;
  read: boolean;
  link?: string;
  type?: 'info' | 'action';
  dismissible?: boolean;
  metadata?: Record<string, unknown>;
}

export const getNotifications = (auth: Auth, callback: (notifications: Notification[]) => void) => {
  if (!auth.currentUser) {
    return () => {
      // No-op unsubscribe when no user
    };
  }

  const q = query(
    collection(firestore, 'users', auth.currentUser.uid, 'notifications'),
    orderBy('timestamp', 'desc')
  );

  const unsubscribe = onSnapshot(
    q,
    (querySnapshot) => {
      try {
        const notifications: Notification[] = [];
        for (const docSnapshot of querySnapshot.docs) {
          notifications.push({ id: docSnapshot.id, ...docSnapshot.data() } as Notification);
        }
        callback(notifications);
      } catch {
        // Silent failure - notifications are non-critical
      }
    },
    () => {
      // Ignore snapshot stream errors on sign-out / token expiry
    }
  );

  return unsubscribe;
};

export const createNotification = async (
  notification: Omit<Notification, 'id' | 'timestamp' | 'read'>
) => {
  try {
    await addDoc(collection(firestore, 'users', notification.userId, 'notifications'), {
      ...notification,
      timestamp: serverTimestamp(),
      read: false,
      dismissible: notification.dismissible ?? true,
    });
  } catch {
    // Silent failure - notification creation is non-critical
  }
};

export const markAsRead = async (userId: string, notificationId: string) => {
  try {
    const notificationRef = doc(firestore, 'users', userId, 'notifications', notificationId);
    const updateData = { read: true };
    await updateDoc(notificationRef, updateData);
  } catch {
    // Silent failure - marking as read is non-critical
  }
};

export const deleteNotification = async (userId: string, notificationId: string) => {
  try {
    const notificationRef = doc(firestore, 'users', userId, 'notifications', notificationId);
    await deleteDoc(notificationRef);
  } catch {
    // Silent failure - notification deletion is non-critical
  }
};

export const deleteNotificationByMetadata = async (
  userId: string,
  metadataFilter: Record<string, unknown>
) => {
  try {
    // We can't query by arbitrary map fields efficiently without indexes for every key.
    // So we fetch all notifications for the user and filter in memory (usually low volume).
    // Or we could use specific fields if we promoted them.
    // Given notification count per user is likely low (<100), client-side filtering or fetching all is okay-ish.
    // Better: Query where 'metadata.sectionId' == value if we knew the key.
    // For now, let's fetch all and delete matches.

    const q = query(collection(firestore, 'users', userId, 'notifications'));
    const querySnapshot = await getDocs(q);
    const batch = writeBatch(firestore);
    let count = 0;

    for (const docSnapshot of querySnapshot.docs) {
      const data = docSnapshot.data() as Notification;
      if (data.metadata) {
        let match = true;
        for (const [key, value] of Object.entries(metadataFilter)) {
          if (data.metadata[key] !== value) {
            match = false;
            break;
          }
        }
        if (match) {
          batch.delete(docSnapshot.ref);
          count++;
        }
      }
    }

    if (count > 0) {
      await batch.commit();
    }
  } catch {
    // Silent failure
  }
};

export const deleteAllNotifications = async (userId: string) => {
  try {
    const q = query(collection(firestore, 'users', userId, 'notifications'));
    const querySnapshot = await getDocs(q);
    const batch = writeBatch(firestore);
    for (const docSnapshot of querySnapshot.docs) {
      batch.delete(docSnapshot.ref);
    }
    await batch.commit();
  } catch {
    // Silent failure - notification deletion is non-critical
  }
};
